import React, { useState, useRef, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { ProgressWebSocket } from '../services/websocket';
import { useStore } from '../hooks/useStore';
import { AdminPanel } from './AdminPanel';
import { FileTransfer } from './FileTransfer';
import { ProfileManager } from './ProfileManager';
import type { Session, User, ProgressUpdate } from '../types';

interface VNCViewerProps {
  user: User;
  onLogout?: () => void;
}

type Phase = 'creating' | 'booting' | 'connected' | 'restarting' | 'shutting_down' | 'stopped';

interface BootMessage {
  text: string;
  status: 'done' | 'active' | 'error';
}

interface ClipboardEntry {
  text: string;
  direction: 'out' | 'in';
  timestamp: number;
}

const IMAGE_NAME = 'sol-kasm-cui:1.0';

const STAGE_MAP: Record<string, string> = {
  starting: 'Starting container services...',
  desktop_ready: 'Desktop environment initializing...',
  email_config: 'Configuring applications...',
  vnc_ready: 'VNC server starting...',
};

const formatTimeAgo = (timestamp: number): string => {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 10) return 'now';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h`;
};

const normalizeGroup = (value: string): string => value.toLowerCase().replace(/[\s_-]/g, '');

const isCUIAdmin = (groups: string[]): boolean => {
  const adminGroups = new Set([
    'administrators',
    'domainadmins',
    'intranetadmin',
    'intranetadmins',
    'cuiadmin',
    'cuiadmins',
    'cuiadminaccess',
  ]);
  return groups.some((group) => adminGroups.has(normalizeGroup(group)));
};

export const VNCViewer: React.FC<VNCViewerProps> = ({ user, onLogout }) => {
  const { setSession } = useStore();

  const [phase, setPhase] = useState<Phase>('creating');
  const [activeSession, setActiveSession] = useState<Session | null>(null);
  const [messages, setMessages] = useState<BootMessage[]>([]);
  const [leftPanelOpen, setLeftPanelOpen] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [powerMenuOpen, setPowerMenuOpen] = useState(false);
  const [profileManagerOpen, setProfileManagerOpen] = useState(false);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'restart' | 'shutdown' | null>(null);
  const [isActioning, setIsActioning] = useState(false);

  // File transfer state
  const [fileTransferOpen, setFileTransferOpen] = useState(false);
  const fileTransferPolicy = user.file_transfer_policy || 'upload_only';

  // Clipboard state
  const [clipboardOpen, setClipboardOpen] = useState(false);
  const [clipboardText, setClipboardText] = useState('');
  const [receivedText, setReceivedText] = useState('');
  const [clipboardStatus, setClipboardStatus] = useState<'idle' | 'sent' | 'received' | 'copied'>('idle');
  const [sendEnabled, setSendEnabled] = useState(true);
  const [receiveEnabled, setReceiveEnabled] = useState(true);
  const [clipboardHistory, setClipboardHistory] = useState<ClipboardEntry[]>([]);

  const userMenuRef = useRef<HTMLDivElement>(null);
  const powerMenuRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const clipboardPanelRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<number | null>(null);
  const wsRef = useRef<ProgressWebSocket | null>(null);
  const lastSentRef = useRef<string>('');

  const clipboardPolicy = user.clipboard_policy || 'paste_only';
  console.log('[Clipboard] Init: policy=%s user.clipboard_policy=%s', clipboardPolicy, user.clipboard_policy);

  // -- Clipboard history helper --

  const addToHistory = useCallback((text: string, direction: ClipboardEntry['direction']) => {
    setClipboardHistory(prev => {
      const entry: ClipboardEntry = { text, direction, timestamp: Date.now() };
      const next = [entry, ...prev];
      if (next.length > 10) next.length = 10;
      return next;
    });
  }, []);

  // -- Message helpers (functional updates, no stale closures) --

  const appendMsg = useCallback((text: string, status: BootMessage['status']) => {
    setMessages(prev => [...prev, { text, status }]);
  }, []);

  const finishLastAndAppend = useCallback((nextText: string, nextStatus: BootMessage['status'] = 'active') => {
    setMessages(prev => {
      if (prev.length === 0) return [{ text: nextText, status: nextStatus }];
      const updated = [...prev];
      updated[updated.length - 1] = { ...updated[updated.length - 1], status: 'done' };
      return [...updated, { text: nextText, status: nextStatus }];
    });
  }, []);

  const finishLast = useCallback((overrideText?: string, status: BootMessage['status'] = 'done') => {
    setMessages(prev => {
      if (prev.length === 0) return prev;
      const updated = [...prev];
      const last = { ...updated[updated.length - 1], status };
      if (overrideText) last.text = overrideText;
      updated[updated.length - 1] = last;
      return updated;
    });
  }, []);

  // -- VNC readiness polling --

  const startVNCPoll = useCallback((sessionId: string) => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }

    let attempts = 0;
    const maxAttempts = 90; // 3 minutes at 2s intervals

    const check = async () => {
      attempts++;
      try {
        const token = api.getToken();
        const url = `/desktop/${sessionId}/vnc.html${token ? `?token=${token}` : ''}`;
        const resp = await fetch(url);
        if (resp.ok) {
          finishLast('VNC server ready');
          appendMsg('Desktop environment loaded', 'done');
          setTimeout(() => setPhase('connected'), 800);
          return;
        }
      } catch {
        // Not ready yet
      }

      if (attempts >= maxAttempts) {
        finishLast('VNC server timed out', 'error');
        appendMsg('Connection failed — try restarting', 'error');
        return;
      }

      pollRef.current = window.setTimeout(check, 2000);
    };

    check();
  }, [appendMsg, finishLast]);

  // -- Session creation with WebSocket progress --

  const watchSessionProgress = useCallback((session: Session) => {
    const progressWs = new ProgressWebSocket(session.id);
    wsRef.current = progressWs;

    progressWs.onUpdate((update: ProgressUpdate) => {
      const label = STAGE_MAP[update.stage];
      if (label) finishLastAndAppend(label, 'active');
    });

    progressWs.onComplete(() => {
      finishLastAndAppend('Connecting to VNC server...', 'active');
      startVNCPoll(session.id);
      wsRef.current = null;
    });

    progressWs.onError(() => {
      finishLast('Connection error during setup', 'error');
    });

    progressWs.connect();
  }, [finishLastAndAppend, finishLast, startVNCPoll]);

  const createSession = useCallback(async () => {
    try {
      const session = await api.connectSession(false);
      setActiveSession(session);
      setSession(session);

      if (session.status === 'running') {
        finishLastAndAppend('Connecting to VNC server...', 'active');
        setPhase('booting');
        startVNCPoll(session.id);
      } else {
        finishLast('Container created');
        appendMsg('Starting services...', 'active');
        watchSessionProgress(session);
      }
    } catch (err: any) {
      finishLast(undefined, 'error');
      appendMsg(`Error: ${err?.message || 'Failed to create session'}`, 'error');
    }
  }, [setSession, finishLastAndAppend, finishLast, appendMsg, startVNCPoll, watchSessionProgress]);

  // -- Initial mount: check for existing session or create --

  useEffect(() => {
    setMessages([{ text: 'Checking for existing session...', status: 'active' }]);

    api.getUserSession()
      .then(session => {
        if (session && session.status === 'running') {
          setActiveSession(session);
          setSession(session);
          finishLastAndAppend('Connecting to VNC server...', 'active');
          setPhase('booting');
          startVNCPoll(session.id);
        } else if (session && session.status === 'paused') {
          finishLastAndAppend('Resuming session...', 'active');
          createSession();
        } else {
          finishLastAndAppend('Creating virtual desktop...', 'active');
          createSession();
        }
      })
      .catch(() => {
        finishLastAndAppend('Creating virtual desktop...', 'active');
        createSession();
      });

    return () => {
      if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // -- Heartbeat: detect session loss (e.g. container paused from inside) --

  useEffect(() => {
    if (phase !== 'connected') return;
    const interval = setInterval(async () => {
      try {
        await api.getUserSession();
      } catch {
        // 401 interceptor clears token/localStorage; trigger React state change
        if (onLogout) onLogout();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [phase, onLogout]);

  // -- Close dropdowns on outside click --

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
      if (powerMenuRef.current && !powerMenuRef.current.contains(e.target as Node)) {
        setPowerMenuOpen(false);
        setConfirmAction(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // -- Derived values --

  const token = api.getToken();
  const clipboardParams = clipboardPolicy === 'full'
    ? '&clipboard_up=true&clipboard_down=true'
    : '&clipboard_up=true&clipboard_down=false';
  const vncUrl = activeSession
    ? `/desktop/${activeSession.id}/vnc.html?resize=scale&path=desktop/${activeSession.id}/websockify${clipboardParams}${token ? `&token=${token}` : ''}`
    : '';

  // -- Clipboard functions --

  const sendToDesktop = useCallback((text: string) => {
    if (!text.trim() || !iframeRef.current?.contentWindow) {
      console.log('[Clipboard] sendToDesktop SKIP: no text or no iframe', { hasText: !!text.trim(), hasIframe: !!iframeRef.current?.contentWindow });
      return;
    }
    if (!sendEnabled) {
      console.log('[Clipboard] sendToDesktop SKIP: sendEnabled=false');
      return;
    }
    if (text === lastSentRef.current) {
      console.log('[Clipboard] sendToDesktop SKIP: duplicate text');
      return;
    }
    console.log('[Clipboard] sendToDesktop SENDING:', text.substring(0, 80));
    iframeRef.current.contentWindow.postMessage(
      { action: 'clipboardsnd', value: text },
      '*'
    );
    lastSentRef.current = text;
    setClipboardText(text);
    setClipboardStatus('sent');
    addToHistory(text, 'out');
    setTimeout(() => setClipboardStatus('idle'), 1500);
  }, [sendEnabled, addToHistory]);

  const handleCopyFromDesktop = async () => {
    if (!receivedText) return;
    try {
      await navigator.clipboard.writeText(receivedText);
      setClipboardStatus('copied');
      setTimeout(() => setClipboardStatus('idle'), 1500);
    } catch {
      // Clipboard write failed
    }
  };

  const toggleClipboard = () => {
    const opening = !clipboardOpen;
    setClipboardOpen(opening);
    if (opening) {
      setFileTransferOpen(false);
      if (!leftPanelOpen) setLeftPanelOpen(true);
    }
  };

  const toggleFileTransfer = () => {
    const opening = !fileTransferOpen;
    setFileTransferOpen(opening);
    if (opening) {
      setClipboardOpen(false);
      if (!leftPanelOpen) setLeftPanelOpen(true);
    }
  };

  const openProfileManager = () => {
    setProfileManagerOpen(true);
    setUserMenuOpen(false);
  };

  const handleProfileDeleted = () => {
    setActiveSession(null);
    setSession(null);
    setPhase('stopped');
    setMessages([]);
  };

  // -- Handle paste into the clipboard textarea --

  const handlePasteToDesktop = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const text = e.clipboardData?.getData('text/plain');
    console.log('[Clipboard] textarea PASTE:', text ? text.substring(0, 80) : '(empty)');
    if (text) {
      sendToDesktop(text);
      e.currentTarget.value = '';
    }
  }, [sendToDesktop]);

  // -- Capture paste events anywhere on our chrome (not inside iframe) --

  useEffect(() => {
    if (phase !== 'connected' || !sendEnabled) return;
    const handlePaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement;
      // Skip if paste is inside the iframe (VNC handles it) or our textarea (React handler handles it)
      if (target.tagName === 'IFRAME') return;
      if (target.classList?.contains('clipboard-paste-area')) return;
      const text = e.clipboardData?.getData('text/plain');
      console.log('[Clipboard] document PASTE on %s: %s', target.tagName, text ? text.substring(0, 80) : '(empty)');
      if (text) sendToDesktop(text);
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [phase, sendEnabled, sendToDesktop]);

  // -- Capture copy events on UI chrome (sends copied text to desktop) --

  useEffect(() => {
    if (phase !== 'connected' || !sendEnabled) return;
    const handleCopy = () => {
      const text = document.getSelection()?.toString();
      console.log('[Clipboard] document COPY event:', text ? text.substring(0, 80) : '(empty)');
      if (text) sendToDesktop(text);
    };
    document.addEventListener('copy', handleCopy);
    return () => document.removeEventListener('copy', handleCopy);
  }, [phase, sendEnabled, sendToDesktop]);

  // -- postMessage listener for clipboard from desktop + clipboardreq --

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.data?.action) return;
      // Filter noisy messages
      if (event.data.action === 'enable_audio' || event.data.action === 'control_open' || event.data.action === 'control_close') return;
      console.log('[Clipboard] postMessage:', event.data.action, event.data.value ? String(event.data.value).substring(0, 80) : '');

      // KasmVNC requesting clipboard content
      if (event.data.action === 'clipboardreq' && sendEnabled) {
        console.log('[Clipboard] clipboardreq — responding with lastSent:', lastSentRef.current ? lastSentRef.current.substring(0, 80) : '(none)');
        if (lastSentRef.current && iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            { action: 'clipboardsnd', value: lastSentRef.current },
            '*'
          );
        }
        return;
      }

      // Receiving clipboard from desktop
      if (event.data.action === 'clipboardrx') {
        console.log('[Clipboard] clipboardrx — policy=%s receiveEnabled=%s text=%s', clipboardPolicy, receiveEnabled, event.data.value ? String(event.data.value).substring(0, 80) : '(empty)');
        if (clipboardPolicy === 'full' && receiveEnabled) {
          const text = event.data.value || '';
          setReceivedText(text);
          setClipboardStatus('received');
          addToHistory(text, 'in');
          setTimeout(() => setClipboardStatus('idle'), 1500);
          // Try to auto-write to system clipboard (requires user gesture — works in Chrome, silent fail in Firefox)
          navigator.clipboard.writeText(text).catch(() => {});
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [clipboardPolicy, receiveEnabled, sendEnabled, addToHistory]);

  // -- Inject clipboard handlers into iframe for paste-anywhere support --
  // Since the iframe is same-origin (proxied through nginx), we can access its
  // contentDocument and intercept Ctrl+V before noVNC processes it.

  useEffect(() => {
    if (phase !== 'connected' || !sendEnabled) return;

    const iframe = iframeRef.current;
    if (!iframe) return;

    let iframeDoc: Document | null = null;
    let bypassKeydown = false;
    let interceptedKey: 'ctrlv' | 'shiftinsert' | null = null;

    const handleIframeKeyDown = (e: Event) => {
      if (bypassKeydown) return; // Let our synthetic events through to noVNC
      const ke = e as KeyboardEvent;

      const isCtrlV = (ke.ctrlKey || ke.metaKey) && (ke.key === 'v' || ke.key === 'V');
      const isShiftInsert = ke.shiftKey && ke.key === 'Insert';

      if (isCtrlV || isShiftInsert) {
        interceptedKey = isShiftInsert ? 'shiftinsert' : 'ctrlv';
        console.log('[Clipboard] iframe keydown intercepted:', ke.key, '→', interceptedKey);
        ke.stopImmediatePropagation(); // Prevent noVNC from sending keystroke to VNC
        // Don't preventDefault — let browser fire paste event so we can read clipboardData
      }
    };

    const handleIframePaste = (e: Event) => {
      const pe = e as ClipboardEvent;
      const text = pe.clipboardData?.getData('text/plain');
      console.log('[Clipboard] iframe PASTE:', text ? text.substring(0, 80) : '(empty)');

      if (!text) return;

      pe.preventDefault();
      pe.stopImmediatePropagation();

      // Update clipboard UI state
      lastSentRef.current = text;
      setClipboardText(text);
      setClipboardStatus('sent');
      addToHistory(text, 'out');
      setTimeout(() => setClipboardStatus('idle'), 1500);

      // Try direct RFB access — guarantees clipboard is set before Ctrl+V keystroke
      const iframeWin = iframe.contentWindow as any;
      const rfb = iframeWin?.rfb || iframeWin?.UI?.rfb || iframeWin?.ui?.rfb;

      // Send the correct keystroke based on what was intercepted
      const wasShiftInsert = interceptedKey === 'shiftinsert';
      interceptedKey = null; // Reset for next paste

      if (rfb && typeof rfb.clipboardPasteFrom === 'function' && typeof rfb.sendKey === 'function') {
        rfb.clipboardPasteFrom(text);
        if (wasShiftInsert) {
          // Shift is already held in VNC — send Insert key
          console.log('[Clipboard] RFB direct: clipboardPasteFrom + sendKey Shift+Insert');
          rfb.sendKey(0xff63, 'Insert', true);
          rfb.sendKey(0xff63, 'Insert', false);
        } else {
          // Ctrl is already held in VNC — send V key
          console.log('[Clipboard] RFB direct: clipboardPasteFrom + sendKey Ctrl+V');
          rfb.sendKey(0x0076, 'KeyV', true);
          rfb.sendKey(0x0076, 'KeyV', false);
        }
      } else {
        // Fallback: postMessage to set clipboard, then synthetic keystroke
        console.log('[Clipboard] Fallback: postMessage clipboardsnd + synthetic', wasShiftInsert ? 'Shift+Insert' : 'Ctrl+V');
        if (iframe.contentWindow) {
          iframe.contentWindow.postMessage({ action: 'clipboardsnd', value: text }, '*');
        }
        // Brief delay to let postMessage/clipboard update propagate before keystroke
        setTimeout(() => {
          if (!iframeDoc) return;
          bypassKeydown = true;
          const target = iframeDoc.activeElement || iframeDoc.body;
          if (wasShiftInsert) {
            target.dispatchEvent(new KeyboardEvent('keydown', {
              key: 'Insert', code: 'Insert', keyCode: 45, shiftKey: true,
              bubbles: true, cancelable: true,
            }));
            target.dispatchEvent(new KeyboardEvent('keyup', {
              key: 'Insert', code: 'Insert', keyCode: 45, shiftKey: true,
              bubbles: true, cancelable: true,
            }));
          } else {
            target.dispatchEvent(new KeyboardEvent('keydown', {
              key: 'v', code: 'KeyV', keyCode: 86, ctrlKey: true,
              bubbles: true, cancelable: true,
            }));
            target.dispatchEvent(new KeyboardEvent('keyup', {
              key: 'v', code: 'KeyV', keyCode: 86, ctrlKey: true,
              bubbles: true, cancelable: true,
            }));
          }
          bypassKeydown = false;
        }, 50);
      }
    };

    const setupIframeClipboard = () => {
      try {
        iframeDoc = iframe.contentDocument;
        if (!iframeDoc) {
          console.log('[Clipboard] iframe contentDocument not accessible');
          return;
        }
        console.log('[Clipboard] Injecting clipboard handlers into iframe document');
        iframeDoc.addEventListener('keydown', handleIframeKeyDown, true);
        iframeDoc.addEventListener('paste', handleIframePaste, true);
      } catch (err) {
        console.log('[Clipboard] Cannot access iframe document:', err);
      }
    };

    // iframe might already be loaded
    try {
      if (iframe.contentDocument?.readyState === 'complete') {
        setupIframeClipboard();
      }
    } catch { /* cross-origin check */ }

    iframe.addEventListener('load', setupIframeClipboard);

    return () => {
      iframe.removeEventListener('load', setupIframeClipboard);
      if (iframeDoc) {
        iframeDoc.removeEventListener('keydown', handleIframeKeyDown, true);
        iframeDoc.removeEventListener('paste', handleIframePaste, true);
      }
    };
  }, [phase, sendEnabled, addToHistory]);

  // -- Power actions --

  const handleRestart = async () => {
    if (confirmAction !== 'restart') {
      setConfirmAction('restart');
      return;
    }

    setPowerMenuOpen(false);
    setConfirmAction(null);
    setIsActioning(true);
    setPhase('restarting');
    setMessages([{ text: 'Shutting down session...', status: 'active' }]);

    try {
      if (activeSession) await api.destroySession(activeSession.id);
      finishLastAndAppend('Creating new virtual desktop...', 'active');
      await createSession();
      setIsActioning(false);
    } catch (err: any) {
      finishLast(undefined, 'error');
      appendMsg(`Error: ${err?.message || 'Unknown error'}`, 'error');
      setIsActioning(false);
    }
  };

  const handleShutdown = async () => {
    if (confirmAction !== 'shutdown') {
      setConfirmAction('shutdown');
      return;
    }

    setPowerMenuOpen(false);
    setConfirmAction(null);
    setIsActioning(true);
    setPhase('shutting_down');
    setMessages([{ text: 'Shutting down...', status: 'active' }]);

    try {
      if (activeSession) await api.destroySession(activeSession.id);
      setActiveSession(null);
      setSession(null);
      setIsActioning(false);
      setPhase('stopped');
    } catch (err: any) {
      finishLast(`Shutdown failed: ${err?.message || 'Unknown'}`, 'error');
      setIsActioning(false);
    }
  };

  const handleStartNow = () => {
    setPhase('creating');
    setMessages([{ text: 'Creating virtual desktop...', status: 'active' }]);
    createSession();
  };

  const handlePowerAction = (action: 'restart' | 'shutdown') => {
    if (action === 'restart') return handleRestart();
    return handleShutdown();
  };

  const handleLogout = async () => {
    setUserMenuOpen(false);
    if (!confirm('Sign out? Your session will be saved.')) return;
    if (activeSession) {
      try { await api.pauseSession(activeSession.id); } catch {}
    }
    if (onLogout) onLogout();
  };

  // -- Boot screen --

  const showSessionInfo = activeSession && phase !== 'shutting_down' && phase !== 'stopped';

  const renderBootScreen = () => (
    <div className="boot-screen">
      <div className="boot-content">
        <div className="boot-header">Solutio Environmental, Inc.</div>
        {showSessionInfo && (
          <div className="boot-info">
            <div className="boot-info-row">
              <span className="boot-label">Image</span>
              <span className="boot-value">{IMAGE_NAME}</span>
            </div>
            <div className="boot-info-row">
              <span className="boot-label">Session</span>
              <span className="boot-value">{activeSession.id}</span>
            </div>
          </div>
        )}
        <div className="boot-divider" />
        <div className="boot-log">
          {messages.map((msg, i) => (
            <div key={i} className={`boot-line boot-${msg.status}`}>
              {msg.status === 'done' && <span className="boot-prefix">OK </span>}
              {msg.status === 'active' && <span className="boot-prefix">&gt;&gt; </span>}
              {msg.status === 'error' && <span className="boot-prefix">!! </span>}
              <span>{msg.text}</span>
              {msg.status === 'active' && <span className="boot-blink">_</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // -- Stopped screen (Proxmox-style) --

  const renderStoppedScreen = () => (
    <div className="stopped-screen">
      <div className="stopped-content">
        <svg className="stopped-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
          <line x1="12" y1="2" x2="12" y2="12" />
        </svg>
        <div className="stopped-label">Virtual Desktop is Stopped</div>
        <button className="start-now-btn" onClick={handleStartNow}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
          Start Now
        </button>
      </div>
    </div>
  );

  // -- Render --

  return (
    <div className="vnc-layout">
      {/* CUI Banner - 22px (32 CFR 2002.20: bold, centered, all caps, #502B85) */}
      <div className="cui-banner">
        <span className="cui-label">CUI</span>
      </div>

      {/* Left Panel */}
      <div className={`side-panel side-panel-left ${leftPanelOpen ? 'expanded' : 'collapsed'}`}>
        <button className="panel-toggle" onClick={() => setLeftPanelOpen(!leftPanelOpen)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {leftPanelOpen
              ? <path d="M15 18l-6-6 6-6" />
              : <path d="M9 18l6-6-6-6" />
            }
          </svg>
        </button>
        <div className="panel-items">
          <button className={`panel-item ${clipboardOpen ? 'active' : ''}`} title="Clipboard" onClick={toggleClipboard}>
            <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            {leftPanelOpen && <span>Clipboard</span>}
          </button>
          <button className={`panel-item ${fileTransferOpen ? 'active' : ''}`} title="File Transfer" onClick={toggleFileTransfer}>
            <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            {leftPanelOpen && <span>Files</span>}
          </button>
          <button className="panel-item" title="Intranet">
            <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            {leftPanelOpen && <span>Intranet</span>}
          </button>
          {vncUrl && (
            <button
              className="panel-item"
              title="Open in New Tab"
              onClick={() => window.open(vncUrl, '_blank')}
            >
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              {leftPanelOpen && <span>New Tab</span>}
            </button>
          )}
        </div>
      </div>

      {/* Clipboard flyout panel */}
      {clipboardOpen && phase === 'connected' && (
        <div className="clipboard-panel" ref={clipboardPanelRef}>
          <div className="clipboard-panel-header">
            <span>Clipboard</span>
            <button className="clipboard-close" onClick={() => setClipboardOpen(false)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Controls */}
          <div className="clipboard-section">
            <label className="clipboard-section-label">Controls</label>
            <div className="clipboard-toggle-row">
              <label className="clipboard-toggle">
                <input
                  type="checkbox"
                  checked={sendEnabled}
                  onChange={() => setSendEnabled(!sendEnabled)}
                />
                <span className="toggle-slider" />
              </label>
              <span className="clipboard-toggle-label">Send to Desktop</span>
            </div>
            {clipboardPolicy === 'full' && (
              <div className="clipboard-toggle-row">
                <label className="clipboard-toggle">
                  <input
                    type="checkbox"
                    checked={receiveEnabled}
                    onChange={() => setReceiveEnabled(!receiveEnabled)}
                  />
                  <span className="toggle-slider" />
                </label>
                <span className="clipboard-toggle-label">Receive from Desktop</span>
              </div>
            )}
          </div>

          {/* Paste to send */}
          {sendEnabled && (
            <div className="clipboard-section">
              <label className="clipboard-section-label">Send to Desktop</label>
              <textarea
                className="clipboard-paste-area"
                placeholder="Ctrl+V here to send to desktop"
                onPaste={handlePasteToDesktop}
                rows={2}
              />
            </div>
          )}

          {/* Current clipboard */}
          {(clipboardText || (clipboardPolicy === 'full' && receivedText)) && (
            <div className="clipboard-section">
              <label className="clipboard-section-label">
                Current
                {clipboardStatus !== 'idle' && (
                  <span className="clipboard-status-badge">
                    {' '}&mdash; {clipboardStatus === 'sent' ? 'Sent!' : clipboardStatus === 'received' ? 'New!' : clipboardStatus === 'copied' ? 'Copied!' : ''}
                  </span>
                )}
              </label>
              {clipboardText && (
                <div className="clipboard-current-item">
                  <span className="clipboard-direction-badge out">OUT</span>
                  <span className="clipboard-current-text">{clipboardText}</span>
                </div>
              )}
              {clipboardPolicy === 'full' && receivedText && (
                <div className="clipboard-current-item">
                  <span className="clipboard-direction-badge in">IN</span>
                  <span className="clipboard-current-text">{receivedText}</span>
                  <button className="clipboard-inline-copy" onClick={handleCopyFromDesktop}>
                    {clipboardStatus === 'copied' ? 'Copied' : 'Copy'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* History */}
          {clipboardHistory.length > 0 && (
            <div className="clipboard-section clipboard-history-section">
              <label className="clipboard-section-label">History ({clipboardHistory.length})</label>
              <div className="clipboard-history-list">
                {clipboardHistory.map((entry, i) => (
                  <div key={i} className="clipboard-history-entry">
                    <span className={`clipboard-direction-badge ${entry.direction}`}>
                      {entry.direction === 'out' ? 'OUT' : 'IN'}
                    </span>
                    <span className="clipboard-history-text">{entry.text}</span>
                    <span className="clipboard-history-time">{formatTimeAgo(entry.timestamp)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hint */}
          <div className="clipboard-hint">
            Ctrl+V anywhere on the page sends clipboard to the desktop.
            {clipboardPolicy === 'full' && ' Text copied inside VNC appears automatically.'}
          </div>
        </div>
      )}

      {/* Main content — boot screen, stopped screen, or VNC iframe */}
      {phase === 'connected' && vncUrl ? (
        <iframe
          ref={iframeRef}
          src={vncUrl}
          className="vnc-iframe-full"
          title="Virtual Desktop"
          sandbox="allow-same-origin allow-scripts allow-forms allow-downloads"
        />
      ) : phase === 'stopped' ? (
        renderStoppedScreen()
      ) : (
        renderBootScreen()
      )}

      {/* File Transfer flyout panel */}
      {fileTransferOpen && (
        <FileTransfer
          fileTransferPolicy={fileTransferPolicy}
          sessionActive={phase === 'connected'}
          onClose={() => setFileTransferOpen(false)}
        />
      )}

      {profileManagerOpen && (
        <ProfileManager
          user={user}
          onClose={() => setProfileManagerOpen(false)}
          onProfileDeleted={handleProfileDeleted}
          onLogout={onLogout}
        />
      )}

      {adminPanelOpen && (
        <AdminPanel
          open={adminPanelOpen}
          onOpenChange={setAdminPanelOpen}
          hideToggle
        />
      )}

      {/* Right Panel — User & Power controls */}
      <div className={`side-panel side-panel-right ${rightPanelOpen ? 'expanded' : 'collapsed'}`}>
        <button className="panel-toggle" onClick={() => setRightPanelOpen(!rightPanelOpen)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {rightPanelOpen
              ? <path d="M9 18l6-6-6-6" />
              : <path d="M15 18l-6-6 6-6" />
            }
          </svg>
        </button>
        <div className="panel-items">
          {/* User dropdown */}
          <div className="panel-dropdown" ref={userMenuRef}>
            <button
              className={`panel-item ${userMenuOpen ? 'active' : ''}`}
              title={user.realname || user.username}
              onClick={() => { setUserMenuOpen(!userMenuOpen); setPowerMenuOpen(false); }}
            >
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              {rightPanelOpen && <span>{user.realname || user.username}</span>}
            </button>
            {userMenuOpen && (
              <div className="panel-dropdown-menu panel-dropdown-right">
                <div className="dropdown-menu-header">{user.username}</div>
                <button className="dropdown-menu-item" onClick={openProfileManager}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                  Profile & Second Factors
                </button>
                <button className="dropdown-menu-item" onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>

          {isCUIAdmin(user.groups) && (
            <button
              className={`panel-item ${adminPanelOpen ? 'active' : ''}`}
              title="CUI Admin"
              onClick={() => {
                setAdminPanelOpen(!adminPanelOpen);
                setUserMenuOpen(false);
                setPowerMenuOpen(false);
              }}
            >
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
              {rightPanelOpen && <span>CUI Admin</span>}
            </button>
          )}

          {/* Power dropdown */}
          <div className="panel-dropdown" ref={powerMenuRef}>
            <button
              className={`panel-item power-item ${powerMenuOpen ? 'active' : ''}`}
              title="Power"
              onClick={() => { setPowerMenuOpen(!powerMenuOpen); setUserMenuOpen(false); setConfirmAction(null); }}
            >
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                <line x1="12" y1="2" x2="12" y2="12" />
              </svg>
              {rightPanelOpen && <span>Power</span>}
            </button>
            {powerMenuOpen && (
              <div className="panel-dropdown-menu panel-dropdown-right">
                {phase === 'stopped' ? (
                  <button
                    className="dropdown-menu-item"
                    onClick={handleStartNow}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="5 3 19 12 5 21 5 3" />
                    </svg>
                    Start
                  </button>
                ) : (
                  <>
                    <button
                      className={`dropdown-menu-item ${confirmAction === 'restart' ? 'confirm' : ''}`}
                      onClick={() => handlePowerAction('restart')}
                      disabled={isActioning}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="23 4 23 10 17 10" />
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                      </svg>
                      {confirmAction === 'restart' ? 'Confirm Restart?' : 'Restart'}
                    </button>
                    <button
                      className={`dropdown-menu-item destructive ${confirmAction === 'shutdown' ? 'confirm' : ''}`}
                      onClick={() => handlePowerAction('shutdown')}
                      disabled={isActioning}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                        <line x1="12" y1="2" x2="12" y2="12" />
                      </svg>
                      {confirmAction === 'shutdown' ? 'Confirm Shutdown?' : 'Shutdown'}
                    </button>
                    <button className="dropdown-menu-item" onClick={handleLogout}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 3v1m0 16v1m-7.07-2.93l.71-.71M18.36 5.64l.71-.71M3 12h1m16 0h1M5.64 5.64l-.71-.71m12.73 12.73l.71.71" />
                      </svg>
                      Hibernate
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CUI Footer (32 CFR 2002.20 best practice) */}
      <div className="cui-footer">CUI</div>
    </div>
  );
};
